import { hashQueryKey } from "@tanstack/react-query";
import produce from "immer";
/**
 * Returns `true` if the given react-query query has a `meta` attribute indicating it is
 * a progressive query.
 */ export function isProgressiveQuery(query) {
    return query.meta && query.meta.dataQueryType === 'progressive';
}
/**
 * Returns true if a progressive query indicates it is fetching more based on
 * the `isFetchingMore` property in the cached data.
 */ export function isProgressiveQueryFetchingMore(query) {
    const queryCacheData = query.state.data;
    // This data isn't the right shape for us to check for the associated properties, so it's not
    // progressively loading.
    if (!queryCacheData || typeof queryCacheData !== 'object' || !('internalMetadata' in queryCacheData) || !queryCacheData.internalMetadata || typeof queryCacheData.internalMetadata !== 'object') {
        return false;
    }
    // We've done enough type checking above we can pretty safely be sure that this data is
    // internal metadata.
    const internalMetadata = queryCacheData.internalMetadata;
    // All this checking is necessary because we cannot guarantee the type of the response from the
    // query cache.
    return !!internalMetadata.isFetchingMore;
}
/**
 * Triggers a custom event to enable us to handle cancellation of progressive queries, which cannot
 * be handled by the default cancellation logic in react-query because it does not natively support
 * progressive loading.
 */ export function cancelProgressiveQuery(queryClient, query) {
    const cancelEvent = {
        type: 'cancelled',
        query
    };
    // Doing this type casting because the react-query types do not support this custom type
    // we added for cancellation. See comments on type defs for more context.
    queryClient.getQueryCache().notify(cancelEvent);
}
export const PROGRESSIVE_DATA_QUERY_CANCELED_MSG = 'The query was canceled before all data was loaded. Refresh to load the results again.';
/**
 * Function that handles important use cases around stopping a progressive query that aren't
 * handled by react-query because it does not support progressive loading. This functionality
 * currently handles the following cases where the plugin should be stopped:
 * - cancellation: the query has been cancelled via `useCancelInProgressDataQueries`
 * - all uses unmounted: the query is no longer in use because all uses of it (e.g. via `useDataQueryResult`)
 *   have been unmounted.
 *
 * Returns an unsubscribe function that should be called when a progressive query is complete and the
 * subscriptions are still not needed. Failing to unsubscribe could cause memory leaks and/or unexepcted
 * behavior.
 */ export function handleProgressiveQueryStopping(dataQueryPlugin, { queryClient, queryKey }) {
    // Precalculating the queryKeyHash because it makes it quicker and cheaper to check if the
    // query cache events are for this query. This works as long as we're using the default
    // query key hashing algorithm. If that changes, we'll want to change this to call whatever
    // custom hashing function we're using.
    const queryKeyHash = hashQueryKey(queryKey);
    const unsubscribeQueryCache = queryClient.getQueryCache().subscribe((queryCacheEvent)=>{
        // Skip events that do not match the active query this queryFn is handling.
        if (queryCacheEvent.query.queryHash !== queryKeyHash) {
            return;
        }
        // When all query observers are removed, the query is in an "unmounted" state where it is no
        // longer being used, and we should cancel the progressive query.
        const isQueryUnmounted = // The event indicates this query is no longer being observed (i.e. the hook that
        // triggered it is unmounted).
        queryCacheEvent.type === 'observerRemoved' && // The number of observers is zero (i.e. there isn't another hook still listening
        // to this query).
        queryCacheEvent.query.getObserversCount() === 0;
        // When a query is cancelled, we need to do some additional cancellation because the default
        // cancellation in react-query will not work for progressive loading.
        const isQueryCanceled = // We do not need to worry about the number of observers for cancellation because cancellation
        // impacts all uses of the query.
        // See typedoc for VizFrameworkQueryCacheNotifyEvent for an explanation for the type-casting.
        queryCacheEvent.type === 'cancelled';
        if (isQueryUnmounted || isQueryCanceled) {
            cleanupProgressiveQuery(dataQueryPlugin, unsubscribeQueryCache);
            // Adjust the data in the cache to communicate the query was cancelled.
            if (isProgressiveQueryFetchingMore(queryCacheEvent.query)) {
                queryClient.setQueryData(queryKey, (prevData)=>{
                    return produce(prevData, (draft)=>{
                        if (draft) {
                            draft.internalMetadata = {
                                isFetchingMore: false,
                                additionalError: new Error(PROGRESSIVE_DATA_QUERY_CANCELED_MSG)
                            };
                        }
                    });
                });
            }
        }
    });
    return unsubscribeQueryCache;
}
// Common actions taken after completion of a progressive query to ensure everything is cleaned up.
export function cleanupProgressiveQuery(dataQueryPlugin, unsubscribeQueryCache) {
    // Ensure the data query plugin cancels any in flight work.
    dataQueryPlugin.cancel();
    // We're done, so stop listening to the cache.
    unsubscribeQueryCache();
}

//# sourceMappingURL=progressive-query-utils.js.map